// Tipizzato sui contratti di §12.1. Ogni chiamata va al server: niente dati di esempio.
import type { UpdateOverview } from '@integrations/watchpod';
import type {
  BlackboardEntry,
  Claim,
  Message,
  MissionTask,
  Session,
  TraceEntry,
  WorkspaceFile,
} from '../types';
import { noteStatus, noteUnauthorized } from './updating';

export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';
const BASE = API_BASE;

export interface SessionInfo {
  requireLogin: boolean;
  authenticated: boolean;
  user?: string | null;
  version: string;
  // §3.0 — la prontezza la calcola il server: la plancia non la deduce dai dati che ha in
  // mano, altrimenti due punti del sistema possono dissentire su se PopTown può lavorare.
  ready: boolean;
  // nessun amministratore ancora: il primo login lo crea con utente e password scelti adesso
  needsOwner?: boolean;
  role?: Role | null;
  display?: string | null;
  avatar?: string | null;
}

/** I ruoli degli account: amministratore, utente (niente Impostazioni), visitatore (sola lettura). */
export type Role = 'admin' | 'user' | 'viewer';

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Amministratore',
  user: 'Utente',
  viewer: 'Visitatore',
};

export interface Account {
  user: string;
  display: string;
  role: Role;
  avatar: string | null;
  createdAt: number | null;
  lastSeen: number | null;
}

export interface Passkey {
  id: string;
  name: string;
  createdAt: number | null;
  usedAt: number | null;
}

// Le chiamate in cui un 401 è una risposta e non la sessione scaduta (password sbagliata).
const AUTH_PATHS = new Set(['/login', '/session-info', '/password', '/passkeys/login']);

// Un errore dell'API: il server lo spiega in `detail`, e lo si porta fino a chi lo mostra.
// Una risposta spiegata dall'app non è il proxy (un 502 di WatchPOD resta un errore suo).
async function fail(r: Response, path: string): Promise<ApiError> {
  const detail = await r
    .json()
    .then((b: { detail?: unknown }) => (typeof b.detail === 'string' ? b.detail : undefined))
    .catch(() => undefined);
  if (detail === undefined) noteStatus(r.status);
  if (r.status === 401 && !AUTH_PATHS.has(path)) noteUnauthorized();
  return new ApiError(r.status, path, detail);
}

async function get<T>(path: string): Promise<T> {
  const r = await fetch(BASE + path, { credentials: 'same-origin' });
  if (!r.ok) throw await fail(r, path);
  return (await r.json()) as T;
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(BASE + path, {
    method,
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!r.ok) throw await fail(r, path);
  return (r.status === 204 ? undefined : await r.json()) as T;
}

// un file in multipart: il browser mette da sé il content-type con il separatore
async function upload<T>(path: string, form: FormData): Promise<T> {
  const r = await fetch(BASE + path, { method: 'POST', credentials: 'same-origin', body: form });
  if (!r.ok) throw await fail(r, path);
  return (await r.json()) as T;
}

export interface Attachment {
  id: string;
  name: string;
  bytes: number;
}

// Impostazioni › Accesso: una sessione aperta dell'amministratore (l'id non è quello del cookie)
export interface AccountSession {
  id: string;
  createdAt: number | null;
  expiresAt: number;
  agent: string;
  current: boolean;
}

export type NoticeKind = 'waiting' | 'completed' | 'failed' | 'anomaly' | 'tools';

export class ApiError extends Error {
  constructor(
    public status: number,
    public path: string,
    public detail?: string,
  ) {
    super(`${status} ${path}`);
    this.name = 'ApiError';
  }
}

export interface ProviderKind {
  id: string;
  label: string;
  hint: string;
  enabled: boolean;
  /** come si collega: abbinamento (SurgeAI), codice dispositivo (Codex), token (Claude Code),
   * account Google o chiave API (Gemini) */
  method: 'pairing' | 'device' | 'token' | 'key' | 'google';
}
/** Un modello del catalogo del provider: id con la versione, nome, livelli di ragionamento. */
export interface CatalogModel {
  id: string;
  label: string;
  efforts: string[];
  defaultEffort: string | null;
}
/** Il predefinito di PopTown: lo usano i pop e le attività che non scelgono un modello. */
export interface DefaultChoice {
  model: string | null;
  effort: string | null;
  thinking: boolean;
  /** il modello usato davvero: con la scelta vuota, quello della configurazione o del locale */
  resolved?: string | null;
}
export interface ConnectedProvider {
  id: string;
  kind: string;
  name: string;
  url: string;
  models: string[];
  /** il catalogo letto dal provider (si rilegge a ogni «Prova») */
  catalog: CatalogModel[];
  createdAt: string;
  /** il prefisso dei suoi modelli: il tipo per il primo account CLI, `tipo@id` per gli altri */
  ref?: string | null;
}
export interface PendingProvider {
  id: string;
  kind: string;
  url: string;
  userCode: string | null;
  /** il nome dell'account scelto all'aggiunta (CLI) */
  name?: string | null;
  /** Gemini con l'account Google: si aspetta il codice che Google mostra dopo l'accesso */
  needsCode?: boolean;
  expiresAt: string;
}
/** Impostazioni › «Limiti dei modelli» (2026-11-02): cosa fare quando un modello finisce i token
 * o è sovraccarico. `fallbacks`: per ogni modello le alternative, in ordine. */
/** I numeri che decidono il consumo di una filiera (0.2.23). */
export interface PipelineBudget {
  id: string;
  name: string;
  steps_per_task: number;
  steps_per_review: number;
  correction_rounds: number;
  /** dove vanno i controlli: dopo ogni pezzo, a fine fase, una volta sola alla fine */
  review_mode: 'piece' | 'phase' | 'end';
}

export interface ModelLimits {
  mode: 'switch' | 'wait';
  fiveHourPct: number;
  weeklyPct: number;
  busyRetryMinutes: number;
  fallbacks: Record<string, string[]>;
}

export interface ProviderListing {
  kinds: ProviderKind[];
  providers: ConnectedProvider[];
  pending: PendingProvider[];
}
export type PollResult =
  | { status: 'pending' | 'refused' | 'expired'; error?: string }
  | { status: 'connected'; provider: ConnectedProvider };

export interface ProviderCheck {
  ok: boolean;
  latencyMs: number;
  models?: string[];
  error?: string;
}

/** Lo stato di WatchPOD più le preferenze di PopTown (aggiornamento automatico, cadenza). */
export type UpdatePrefs = { autoUpdate: boolean; checkMinutes: number };
export type UpdatesView = UpdateOverview & UpdatePrefs;

/** Una persona del team: il profilo da cui il PM istanzia gli agenti di ogni lavoro. */
export type AgentProfile = {
  id: string;
  label: string;
  team: string;
  duty: string;
  instructions: string;
  tools: string[];
  model: string | null;
  thinking: boolean;
  /** il livello di ragionamento scelto fra quelli del modello; null: quello del modello */
  effort: string | null;
  /** L'avatar del pop (es. `developer-02`): il suo o quello proposto dal ruolo. */
  avatar: string;
  /** il nome proprio del pop (es. Dario): il suo o quello di partenza */
  nickname: string;
  effectiveModel: string | null;
  coordinator: boolean;
};
export type AgentProfileInput = Omit<AgentProfile, 'effectiveModel' | 'coordinator' | 'avatar'> & {
  avatar: string | null;
};

export type ToolKind = {
  id: string;
  label: string;
  hint: string;
  enabled: boolean;
  /** i filtri del market «Aggiungi strumento» */
  category: string;
  icon: string;
};
/** Un destinatario Pushover: alla plancia arrivano nome, coda della chiave e dispositivi. */
export type PushRecipient = { name: string; key: string; devices?: string[] };
export type ToolItem = {
  name: string;
  description: string;
  enabled: boolean;
  profiles?: string[];
};
export type ToolGroup = {
  id: string;
  kind: string;
  name: string;
  url?: string;
  description: string;
  removable: boolean;
  error?: string | null;
  profiles?: string[];
  tools: ToolItem[];
  /** Gmail: l'indirizzo dell'account collegato */
  account?: string | null;
  recipients?: PushRecipient[] | null;
  /** API: lo schema dei parametri in JSON, vuoto se non c'è */
  params?: string;
};
export type PendingTool = {
  id: string;
  kind: string;
  url: string;
  userCode: string;
  expiresAt: string;
};
export type ToolCatalog = { kinds: ToolKind[]; groups: ToolGroup[]; pending: PendingTool[] };
export type NewTool = {
  kind: string;
  name: string;
  url: string;
  description: string;
  token?: string;
  client_id?: string;
  client_secret?: string;
  /** l'indirizzo della plancia: Google ci torna dopo il consenso */
  origin?: string;
  recipients?: { name: string; key: string }[];
  /** API: come si autentica e il limite della risposta (il segreto va in `token`) */
  auth?: { mode: string; name: string; user: string };
  max_bytes?: number;
  /** API: lo schema dei parametri in JSON, vuoto se non c'è */
  params?: string;
  /** Strumenti del CLI: claude-code, codex o antigravity */
  cli?: string;
};
export type AddedTool = { pending?: PendingTool; authorize?: string; connection?: unknown };

// le attività: lavori che PopTown sa fare e ripetere, a mano, con un input o a orario
export type RoutineTrigger = 'manual' | 'input' | 'schedule' | 'condition' | 'hook';
/** «Quando succede» (S4): una misura di una fonte, confrontata, controllata a cadenza. */
export interface RoutineCondition {
  /** lo strumento che fa partire l'attività: memo, Gmail, un'API o un promemoria di calendario */
  source: 'memo' | 'gmail' | 'api' | 'calendar';
  /** la voce di controllo che PopTown tiene; la riempie il salvataggio da spazio e progetto */
  watch: string;
  /** memo: un progetto, tutti i progetti di uno spazio o tutti gli spazi */
  scope?: 'project' | 'space' | 'all';
  space?: string | null;
  project?: string | null;
  metric: 'anomalies' | 'backlog';
  op: '>=' | '>' | '=' | '!=' | '<' | '<=' | 'contains';
  value: number;
  every_minutes: number;
  sequence: boolean;
  /** Gmail: la connessione, la ricerca (sintassi di Gmail) e l'etichetta facoltativa */
  connection?: string | null;
  query?: string;
  label?: string | null;
  /** un giro per email o uno con tutte */
  batch?: 'each' | 'all';
  /** per quanti giorni ricorda le email già fatte */
  remember_days?: number;
  /** da quando guarda (secondi): lo mette il server al salvataggio */
  since?: number | null;
  /** API: percorso, parametri, campo del JSON, valore del confronto e quando parte */
  path?: string;
  params?: Record<string, string>;
  field?: string;
  target?: string;
  fire?: 'rising' | 'always';
  /** promemoria di calendario: il calendario e quanti minuti prima parte il giro */
  calendar?: string;
  minutes_before?: number;
}
/** Spazi e progetti memo per «Quando succede»: da memo, o quelli che PopTown conosce. */
export interface MemoScopes {
  source: 'memo' | 'poptown';
  spaces: { space: string; projects: string[] }[];
}
/** Un canale di «Notifica»: una connessione Gmail o Pushover e i destinatari ammessi. */
export interface RoutineNotify {
  connection: string;
  /** indirizzi email (Gmail) o nomi dei destinatari della connessione (Pushover) */
  recipients: string[];
  /** solo Pushover: priorità imposta (-2…2, 2 = emergenza) e suono; null = li sceglie il pop */
  priority?: number | null;
  sound?: string | null;
}
/** Cosa fa (S4): Attività (un pop da solo) od operativa (progetto), chi lavora, strumenti, prudenza del push. */
export interface RoutineOptions {
  kind: 'text' | 'operational';
  profile: string | null;
  /** in un'Attività: i pop che si passano il compito, in ordine (il primo è anche `profile`) */
  profiles?: string[];
  tools: string[];
  push: 'project' | 'task' | 'mission' | 'manual';
  pullRequest: 'project' | 'none' | 'open' | 'merge';
  /** il modello con cui lavora il pop dell'attività; null: quello del pop */
  model?: string | null;
  effort?: string | null;
  thinking?: boolean;
  /** dove va il risultato: la risposta nella sessione, più un file o una nota su memo */
  deliver?: 'session' | 'file' | 'memo';
  /** come e a chi avvisa il pop: i canali del passo «Notifica» */
  notify?: RoutineNotify[];
}
export interface RoutineSchedule {
  every_minutes?: number;
  every_hours?: number;
  daily_at?: string;
}
export interface RoutineInput {
  name: string;
  task: string;
  pipeline: string;
  trigger: RoutineTrigger;
  schedule?: RoutineSchedule | null;
  input_hint?: string | null;
  enabled?: boolean;
  project?: string | null;
  condition?: RoutineCondition | null;
  options?: RoutineOptions | null;
}
export interface Routine {
  id: string;
  name: string;
  task: string;
  pipeline: string;
  trigger: RoutineTrigger;
  schedule: RoutineSchedule | null;
  inputHint: string | null;
  project?: string | null;
  condition?: RoutineCondition | null;
  options?: RoutineOptions | null;
  hook?: boolean;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  nextRun: string | null;
  lastRun: { sessionId: string; state: string; at: string } | null;
}
/** I consumi per giorno (`/api/usage`), nel fuso del browser: per i grafici della Dashboard. */
export interface Usage {
  days: string[];
  tokens: number[];
  costEur: number[];
  byProject: Record<string, { tokens: number[]; costEur: number[] }>;
}
/** Integrazioni › memo (S5): progetti seguiti, cosa conta come anomalia, anomalie aperte. */
export interface MemoWatch {
  id: string;
  connection: string | null;
  space: string;
  project: string;
  anomalies?: number | null;
  backlog?: number | null;
  error?: string | null;
}
export interface MemoTicket {
  subject: string;
  handle?: string;
  title: string;
  type: string;
  status: string;
  priority?: number;
  updated_at?: string;
  scope?: string;
}
export interface Integrations {
  connections: { id: string; name: string; url: string }[];
  statuses: string[];
  types: string[];
  watched: MemoWatch[];
  anomalies: MemoTicket[];
  choices: { statuses: string[]; types: string[] };
}
export interface Pipeline {
  id: string;
  name: string;
  description: string;
}

// L'archivio dei git e i progetti (spec «Progetti di PopTown»): il token entra e non torna.
export type GitKind = 'gitea' | 'github' | 'git';
export interface GitTest {
  ok: boolean;
  detail: string;
  at: string;
  login?: string;
  repos?: number | null;
}
export interface GitRegistry {
  id: string;
  name: string;
  kind: GitKind;
  url: string;
  username: string;
  hasToken: boolean;
  createdAt: string;
  lastTest: GitTest | null;
  usedBy: string[];
}
export interface GitRegistryInput {
  name: string;
  kind: GitKind;
  url: string;
  username: string;
  token: string;
}
export interface GitRegistries {
  registries: GitRegistry[];
  kinds: { id: GitKind; label: string }[];
  keyReady: boolean;
}
export interface GitRepo {
  fullName: string;
  private: boolean;
  defaultBranch: string;
  canPush: boolean;
}
export type PushWhen = 'task' | 'mission' | 'manual';
export type PullRequestMode = 'none' | 'open' | 'merge';
/** Un pop nel team di un progetto (poptown/project_plan.py). */
export interface PlanMember {
  name: string;
  profile: string;
  avatar: string | null;
  model: string | null;
  phase: string;
  instructions: string;
}
export interface PlanPhase {
  name: string;
  description: string;
  owner: string;
  model: string | null;
  days: number;
}
/** Requisiti, team e fasi del progetto: nascono nella procedura «Nuovo progetto». */
/** Dove vanno i requisiti su memo: connessione, space, progetto e la spec già scritta. */
export interface PlanMemo {
  enabled: boolean;
  connection: string | null;
  space: string;
  project: string;
  subject: string | null;
}
export interface ProjectPlan {
  requirements: string;
  start: string | null;
  pipeline: string | null;
  team: PlanMember[];
  phases: PlanPhase[];
  memo?: PlanMemo | null;
}
/** Un turno del triage con PM e Team Leader (sessione del progetto). */
export interface TriageTurn {
  sessionId: string;
  ok: boolean;
  error: string | null;
  messages: { author: string; profile: string; text: string }[];
  requirements: string;
  sections: Record<string, 'chiaro' | 'da chiedere' | 'da scrivere'>;
  done: boolean;
}
export interface TeamProposal {
  ok: boolean;
  error: string | null;
  team: { profile: string; count: number; phase: string }[];
  phases: { name: string; description: string; owner: string; days: number }[];
}
export interface ProjectInput {
  name: string;
  description: string;
  workspace: { kind: 'local'; folder: string };
  git: { registry: string; repo: string } | null;
  baseBranch: string;
  workBranch: string;
  commitAuthor: string;
  commitEmail: string;
  commitMessage: string;
  push: PushWhen;
  pullRequest: PullRequestMode;
  docsMaxMb: number;
  default: boolean;
  plan?: ProjectPlan;
}
export interface Project extends ProjectInput {
  id: string;
  createdAt: string;
  archived?: boolean;
  archivedAt?: string | null;
}
/** Una notifica (S7), ricavata dagli eventi del registro. */
export interface Notice {
  id: string;
  icon: string;
  tone: string;
  text: string;
  at: string;
  sessionId: string | null;
  unread: boolean;
}

export const api = {
  providers: (): Promise<ProviderListing> => get<ProviderListing>('/providers'),
  addProvider: (kind: string, url: string, name?: string): Promise<PendingProvider> =>
    send('POST', '/providers', { kind, url, name: name || null }),
  addProviderToken: (kind: string, token: string, name?: string): Promise<PollResult> =>
    send('POST', '/providers/token', { kind, token, name: name || null }),
  /** «Modifica»: il nome dell'account e, per chi usa un token, un token nuovo */
  updateProvider: (
    id: string,
    body: { name?: string; token?: string | null },
  ): Promise<ConnectedProvider> => send('PATCH', `/providers/${id}`, body),
  pollProvider: (id: string): Promise<PollResult> => send('POST', `/providers/pending/${id}/poll`),
  defaultModel: (): Promise<DefaultChoice> => get('/providers/default'),
  setDefaultModel: (body: DefaultChoice): Promise<DefaultChoice> =>
    send('PUT', '/providers/default', body),
  submitProviderCode: (id: string, code: string): Promise<PollResult> =>
    send('POST', `/providers/pending/${id}/code`, { code }),
  cancelProvider: (id: string): Promise<void> => send('DELETE', `/providers/pending/${id}`),
  removeProvider: (id: string): Promise<void> => send('DELETE', `/providers/${id}`),
  testProvider: (id: string): Promise<ProviderCheck> => send('POST', `/providers/${id}/test`),

  sessionInfo: (): Promise<SessionInfo> => get<SessionInfo>('/session-info'),
  login: (body: { user: string; password: string }): Promise<{ ok: true }> =>
    send('POST', '/login', body),
  logout: (): Promise<void> => send('POST', '/logout'),
  changePassword: (body: { current: string; next: string }): Promise<{ ok: true }> =>
    send('POST', '/password', body),

  sessions: (): Promise<Session[]> => get('/sessions'),
  messages: (): Promise<Message[]> => get('/messages'),
  trace: (): Promise<TraceEntry[]> => get('/trace'),
  blackboard: (): Promise<BlackboardEntry[]> => get('/blackboard'),
  claims: (): Promise<Claim[]> => get('/claims'),
  tasks: (id: string): Promise<MissionTask[]> => get(`/sessions/${id}/tasks`),
  integrations: (): Promise<Integrations> => get('/integrations'),
  memoScopes: (connection?: string | null): Promise<MemoScopes> =>
    get(
      `/integrations/memo/scopes${connection ? `?connection=${encodeURIComponent(connection)}` : ''}`,
    ),
  notifications: (): Promise<{ items: Notice[]; unread: number }> => get('/notifications'),
  notificationsSeen: (): Promise<{ seen: string }> => send('POST', '/notifications/seen'),
  modelLimits: (): Promise<ModelLimits> => get('/model-limits'),
  pipelineBudgets: (): Promise<PipelineBudget[]> => get('/model-limits/pipelines'),
  setPipelineBudgets: (
    body: Record<string, Partial<Omit<PipelineBudget, 'id' | 'name'>>>,
  ): Promise<PipelineBudget[]> => send('PUT', '/model-limits/pipelines', body),
  setModelLimits: (body: ModelLimits): Promise<ModelLimits> => send('PUT', '/model-limits', body),
  notificationKinds: (): Promise<{ kinds: NoticeKind[]; choices: NoticeKind[] }> =>
    get('/notifications/kinds'),
  setNotificationKinds: (kinds: NoticeKind[]): Promise<{ kinds: NoticeKind[] }> =>
    send('PUT', '/notifications/kinds', { kinds }),
  accountSessions: (): Promise<AccountSession[]> => get('/account/sessions'),
  closeSession: (id: string): Promise<{ closed: number }> =>
    send('DELETE', `/account/sessions/${id}`),
  closeOtherSessions: (): Promise<{ closed: number }> => send('DELETE', '/account/sessions'),
  backup: (): Promise<Record<string, unknown>> => get('/backup'),
  archiveProject: (id: string, archived: boolean): Promise<Project> =>
    send('POST', `/projects/${id}/archive`, { archived }),
  saveIntegrations: (body: {
    statuses: string[];
    types: string[];
    watched: Omit<MemoWatch, 'anomalies' | 'backlog' | 'error'>[];
  }): Promise<Integrations> => send('PUT', '/integrations/memo', body),
  hookToken: (id: string): Promise<{ token: string; path: string }> =>
    send('POST', `/routines/${id}/hook-token`),
  usage: (days: number, tz: string): Promise<Usage> =>
    get(`/usage?days=${days}&tz=${encodeURIComponent(tz)}`),
  files: (id: string): Promise<WorkspaceFile[]> => get(`/sessions/${id}/files`),

  gitRegistries: (): Promise<GitRegistries> => get('/git-registries'),
  addGitRegistry: (body: GitRegistryInput): Promise<GitRegistry> =>
    send('POST', '/git-registries', body),
  saveGitRegistry: (id: string, body: GitRegistryInput): Promise<GitRegistry> =>
    send('PUT', `/git-registries/${id}`, body),
  removeGitRegistry: (id: string, confirm = false): Promise<{ ok: true }> =>
    send('DELETE', `/git-registries/${id}${confirm ? '?confirm=true' : ''}`),
  testGitRegistry: (id: string): Promise<GitTest> => send('POST', `/git-registries/${id}/test`),
  gitRepos: (id: string, q = ''): Promise<{ repos: GitRepo[] }> =>
    get(`/git-registries/${id}/repos?q=${encodeURIComponent(q)}`),
  createGitRepo: (id: string, repo: string, isPrivate: boolean): Promise<GitRepo> =>
    send('POST', `/git-registries/${id}/repos`, { repo, private: isPrivate }),
  projects: (): Promise<{
    projects: Project[];
    defaults: Omit<ProjectInput, 'name' | 'description'>;
    /** i pop che conducono il triage (agents.yaml › triage) */
    triage?: { lead: string; technical: string };
  }> => get('/projects'),
  addProject: (body: ProjectInput): Promise<Project> => send('POST', '/projects', body),
  saveProject: (id: string, body: ProjectInput): Promise<Project> =>
    send('PUT', `/projects/${id}`, body),
  removeProject: (id: string): Promise<{ ok: true }> => send('DELETE', `/projects/${id}`),
  projectBrief: (id: string): Promise<{ text: string }> => get(`/projects/${id}/brief`),
  projectTriage: (
    id: string,
    body: {
      sessionId: string | null;
      reply: string | null;
      requirements: string;
      docs: string;
      /** senza modello vale quello del pop */
      models: {
        lead: { model: string | null; effort: string | null; thinking: boolean };
        technical: { model: string | null; effort: string | null; thinking: boolean };
      };
    },
  ): Promise<TriageTurn> => send('POST', `/projects/${id}/triage`, body),
  projectTriageClose: (id: string, sessionId: string): Promise<{ ok: true }> =>
    send('POST', `/projects/${id}/triage/close`, { sessionId }),
  projectProposal: (
    id: string,
    requirements: string,
    model: string | null,
  ): Promise<TeamProposal> => send('POST', `/projects/${id}/proposal`, { requirements, model }),
  memoLinks: (): Promise<{ id: string; name: string }[]> => get('/memo-links'),
  gitReadme: (registry: string, repo: string): Promise<{ text: string }> =>
    get(`/git-registries/${registry}/readme?repo=${encodeURIComponent(repo)}`),
  projectMemo: (id: string): Promise<{ ok: boolean; error: string | null }> =>
    send('POST', `/projects/${id}/memo`),
  uploadAttachment: (file: File, project: string): Promise<Attachment> => {
    const form = new FormData();
    form.append('file', file);
    form.append('project', project);
    return upload('/attachments', form);
  },
  pushSession: (id: string): Promise<{ detail: string }> => send('POST', `/sessions/${id}/push`),

  createSession: (body: {
    text: string;
    channel: string;
    project?: string;
    mode?: 'plan' | 'together' | 'chat';
    /** Conversazione: il pop che risponde */
    profile?: string;
    attachments?: string[];
  }): Promise<{ id: string; queuedTo?: string }> => send('POST', '/sessions', body),
  // il proprio profilo
  saveAccount: (display: string): Promise<Account> => send('PUT', '/account', { display }),
  saveAvatar: (body: { pop?: string; image?: string | null }): Promise<Account> =>
    send('PUT', '/account/avatar', body),
  passkeys: (): Promise<Passkey[]> => get('/account/passkeys'),
  passkeyOptions: (): Promise<Record<string, unknown>> => send('POST', '/account/passkeys/options'),
  addPasskey: (credential: unknown, name: string): Promise<Passkey> =>
    send('POST', '/account/passkeys', { credential, name }),
  removePasskey: (id: string): Promise<void> =>
    send('DELETE', `/account/passkeys/${encodeURIComponent(id)}`),
  passkeyLoginOptions: (): Promise<Record<string, unknown>> =>
    send('POST', '/passkeys/login/options'),
  passkeyLogin: (credential: unknown): Promise<{ ok: true }> =>
    send('POST', '/passkeys/login', { credential }),
  // gli account (solo l'amministratore)
  accounts: (): Promise<Account[]> => get('/users'),
  createAccount: (body: {
    user: string;
    display: string;
    role: Role;
    password: string;
  }): Promise<Account> => send('POST', '/users', body),
  setAccountRole: (user: string, role: Role): Promise<Account> =>
    send('PUT', `/users/${encodeURIComponent(user)}`, { role }),
  removeAccount: (user: string): Promise<void> =>
    send('DELETE', `/users/${encodeURIComponent(user)}`),

  reply: (
    id: string,
    body: { text?: string; choice?: string; profile?: string; end?: boolean },
  ): Promise<{ ok: true; messageId?: string }> => send('POST', `/sessions/${id}/reply`, body),
  cancel: (id: string): Promise<{ ok: true }> => send('POST', `/sessions/${id}/cancel`),
  resume: (id: string): Promise<{ ok: true }> => send('POST', `/sessions/${id}/resume`),

  // WatchPOD: ogni lettura di /updates è anche un controllo
  updates: (): Promise<UpdatesView> => get('/updates'),
  saveUpdatePrefs: (patch: Partial<UpdatePrefs>): Promise<UpdatesView> =>
    send('POST', '/updates/settings', patch),
  installUpdate: (version: string): Promise<{ version: string }> =>
    send('POST', '/updates/install', { version }),
  snoozeUpdate: (version: string): Promise<unknown> => send('POST', '/updates/snooze', { version }),
  saveWatchpodToken: (token: string): Promise<UpdatesView> =>
    send('POST', '/updates/token', { token }),
  saveWatchpodUrl: (url: string): Promise<UpdatesView> => send('POST', '/updates/url', { url }),
  retryWatchpod: (): Promise<UpdatesView> => send('POST', '/updates/retry'),

  // strumenti: le connessioni MCP e cosa concedono agli agenti
  tools: (): Promise<ToolCatalog> => get('/tools'),
  addTool: (body: NewTool): Promise<AddedTool> => send('POST', '/tools/connections', body),
  reconnectGoogle: (id: string, origin: string): Promise<{ authorize: string }> =>
    send('POST', `/tools/connections/${id}/google`, { origin }),
  setRecipients: (id: string, recipients: { name: string; key: string }[]): Promise<unknown> =>
    send('PUT', `/tools/connections/${id}/recipients`, { recipients }),
  pollTool: (id: string): Promise<{ status: string }> => send('POST', `/tools/pending/${id}/poll`),
  cancelTool: (id: string): Promise<void> => send('DELETE', `/tools/pending/${id}`),
  refreshTool: (id: string): Promise<unknown> => send('POST', `/tools/connections/${id}/refresh`),
  changeTool: (
    id: string,
    body: { description?: string; enabled?: string[]; params?: string },
  ): Promise<unknown> => send('PATCH', `/tools/connections/${id}`, body),
  removeTool: (id: string): Promise<void> => send('DELETE', `/tools/connections/${id}`),

  // agenti
  profiles: (): Promise<AgentProfile[]> => get('/profiles'),
  /** i nomi da cui si pescano quelli dei pop doppioni in un team */
  popNames: (): Promise<string[]> => get('/pop-names'),
  saveProfile: (body: AgentProfileInput): Promise<AgentProfile> =>
    send('PUT', `/profiles/${encodeURIComponent(body.id)}`, body),
  removeProfile: (id: string): Promise<void> =>
    send('DELETE', `/profiles/${encodeURIComponent(id)}`),

  // attività e filiere
  pipelines: (): Promise<Pipeline[]> => get('/pipelines'),
  routines: (): Promise<Routine[]> => get('/routines'),
  createRoutine: (body: RoutineInput): Promise<Routine> => send('POST', '/routines', body),
  updateRoutine: (id: string, body: RoutineInput): Promise<Routine> =>
    send('PUT', `/routines/${id}`, body),
  enableRoutine: (id: string, enabled: boolean): Promise<Routine> =>
    send('PUT', `/routines/${id}/enabled`, { enabled }),
  deleteRoutine: (id: string): Promise<void> => send('DELETE', `/routines/${id}`),
  runRoutine: (id: string, input?: string): Promise<{ id: string }> =>
    send('POST', `/routines/${id}/run`, { input }),
};
